import { useCallback, useMemo, useEffect, useState } from 'react';
import { MangoQuerySelector, RxCollection } from 'rxdb';
import { useRxData } from 'rxdb-hooks';
import { escapeRegExp, calculateDistance, getCurrentLocation } from '@helper/utils';
import { useDebounceSearch } from '@hooks/useDebounceSearch';
import { useRxCountQuery } from '@hooks/useCount';
import { ContractorTypeLabel } from '@components/lms/addNewSite/components/meetingDetails';
import { ContractorTypeFilterLabel } from '@components/lms/siteListWrapper/components/siteListFilters';
import { ISiteListRxDb, ISiteListResponse, SiteListFieldMap } from '.';
import { useFilterStore } from '@store/filter';
import { useSiteListFilterStore, SiteListFilterObject } from '@store/lms/siteListFilterStore';
import { useLocation } from 'react-router-dom';

export const useSiteListRxDb = (filterParam?: SiteListFilterObject | string, pageSize?: number): ISiteListRxDb => {
	const { search = '', sortValue: sortBy } = useFilterStore((state) => state);
	const { siteListFilter: defaultFilterObject } = useSiteListFilterStore();
	const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
	const location = useLocation();

	// Handle backward compatibility: if filterParam is a string, convert it to filter object
	// Otherwise use the provided filter object or fall back to default
	const filterObject = useMemo(() => {
		if (!filterParam) {
			return defaultFilterObject;
		}
		// If it's a string (old way), create a filter object with the appropriate field
		if (typeof filterParam === 'string') {
			if (filterParam === 'Competition' && SiteListFieldMap[filterParam]) {
				return { ...defaultFilterObject, solvent_used_c: 'Competition' };
			}
			if (filterParam === 'Upcoming' && SiteListFieldMap[filterParam]) {
				return { ...defaultFilterObject, site_current_status_c: 'Upcoming' };
			}
			// Empty string or other values - use default
			return defaultFilterObject;
		}
		// It's already a filter object (new way)
		return filterParam;
	}, [filterParam, defaultFilterObject]);

	const debouncedSearch = useDebounceSearch(search);

	useEffect(() => {
		getCurrentLocation().then(setCurrentLocation);
	}, []);

	// Get route-based default filters
	const getRouteBasedFilters = useCallback(() => {
		const pathname = location.pathname;

		if (pathname.includes('/lms/sites/upcoming')) {
			return {
				site_current_status_c: 'Upcoming',
			};
		}

		if (pathname.includes('/lms/sites/competition')) {
			return {
				solvent_used_c: 'Competition',
			};
		}

		return {};
	}, [location.pathname]);

	const queryConstructor = useCallback(
		(collection: RxCollection<ISiteListResponse>) => {
			const selector: MangoQuerySelector<ISiteListResponse> = {};

			if (debouncedSearch && debouncedSearch.trim() !== '') {
				const searchData = escapeRegExp(debouncedSearch);
				const searchTrimmed = debouncedSearch.trim();
				const searchNumber = Number(searchTrimmed);
				const isNumeric = !isNaN(searchNumber) && isFinite(searchNumber);

				if (isNumeric) {
					// For numeric searches, use multiple range queries to cover different number lengths
					// e.g., "148" should match: 148, 1480-1489, 14800-14899, 148000-148999, etc.
					const searchNum = searchNumber;

					// Create ranges for different number lengths
					// For "148": match 148, 1480-1489, 14800-14899, 148000-148999, 1480000-1489999
					const idRanges: Array<{ id: { $eq: number } } | { id: { $gte: number; $lte: number } }> = [
						{ id: { $eq: searchNum } }, // Exact match
					];

					// Add ranges for numbers with more digits
					for (let i = 1; i <= 6; i++) {
						const multiplier = Math.pow(10, i);
						const minId = searchNum * multiplier;
						const maxId = minId + multiplier - 1;
						idRanges.push({ id: { $gte: minId, $lte: maxId } });
					}

					// Build $or with ID ranges and string field matches
					selector.$or = [
						...idRanges,
						{ name: { $regex: `.*${searchData}.*`, $options: 'i' } },
						{ site_location_c: { $regex: `.*${searchData}.*`, $options: 'i' } },
						{ address_c: { $regex: `.*${searchData}.*`, $options: 'i' } },
						{ applicator_phone_number_text_c: { $regex: `.*${searchData}.*`, $options: 'i' } },
						{ end_user_phone_c: { $regex: `.*${searchData}.*`, $options: 'i' } },
						{ end_user_name_c: { $regex: `.*${searchData}.*`, $options: 'i' } },
						{ applicatorname_c: { $regex: `.*${searchData}.*`, $options: 'i' } },
						{ whom_did_you_meet_c: { $regex: `.*${searchData}.*`, $options: 'i' } },
					];
				} else {
					// For non-numeric searches, use regex on string fields
					selector.$or = [
						{ name: { $regex: `.*${searchData}.*`, $options: 'i' } },
						{ site_location_c: { $regex: `.*${searchData}.*`, $options: 'i' } },
						{ address_c: { $regex: `.*${searchData}.*`, $options: 'i' } },
						{ applicator_phone_number_text_c: { $regex: `.*${searchData}.*`, $options: 'i' } },
						{ end_user_phone_c: { $regex: `.*${searchData}.*`, $options: 'i' } },
						{ end_user_name_c: { $regex: `.*${searchData}.*`, $options: 'i' } },
						{ applicatorname_c: { $regex: `.*${searchData}.*`, $options: 'i' } },
						{ whom_did_you_meet_c: { $regex: `.*${searchData}.*`, $options: 'i' } },
					];
				}
			}

			// Get route-based default filters
			const routeFilters = getRouteBasedFilters();

			if (filterObject) {
				if (filterObject.lastActivityDate) {
					const [startDate, endDate] = filterObject.lastActivityDate;
					if (startDate && endDate) {
						selector.lastactivitydate = {
							$gte: startDate.toISOString(),
							$lte: endDate.toISOString(),
						};
					}
				}
				if (filterObject.createdAt) {
					const [startDate, endDate] = filterObject.createdAt;
					if (startDate && endDate) {
						selector.createddate = {
							$gte: startDate.toISOString(),
							$lte: endDate.toISOString(),
						};
					}
				}
				if (filterObject.date_of_next_visit) {
					const [startDate, endDate] = filterObject.date_of_next_visit;
					if (startDate && endDate) {
						selector.date_of_next_visit = {
							$gte: startDate.toISOString(),
							$lte: endDate.toISOString(),
						};
					}
				}
				if (filterObject.siteType) {
					selector.site_sub_type_c = { $eq: filterObject.siteType };
				}
				if (filterObject.contractorType) {
					if (filterObject.contractorType === ContractorTypeFilterLabel.FCC_Contractor)
						selector.is_fcc_contactor = { $eq: true };
					else if (filterObject.contractorType === ContractorTypeFilterLabel.Non_FCC_Contractor)
						selector.is_fcc_contactor = { $eq: false };
					else if (filterObject.contractorType === ContractorTypeFilterLabel.Adopted)
						selector.adopted_site = { $eq: true };
					else if (filterObject.contractorType === ContractorTypeFilterLabel.Non_Aopted_Contractor) {
						selector.adopted_site = { $eq: false };
						selector.whom_did_you_meet_c = ContractorTypeLabel.Contractor;
					} else if (filterObject.contractorType === ContractorTypeFilterLabel.No_Contractor)
						selector.whom_did_you_meet_c = { $ne: ContractorTypeLabel.Contractor };
				}
				if (filterObject.convertStatus) {
					selector.convert_status = { $eq: true };
				}
				if (filterObject.source) {
					selector.source = { $eq: filterObject.source };
				}
				if (filterObject.site_current_status_c) {
					selector.site_current_status_c = { $eq: filterObject.site_current_status_c };
				}
				if (filterObject.solvent_used_c) {
					selector.solvent_used_c = { $eq: filterObject.solvent_used_c };
				}
			}

			// Apply route-based default filters only if no store filters are set
			// Store filters take precedence (for dashboard navigation and explicit filtering)
			const hasStoreFilters =
				filterObject &&
				(filterObject.site_current_status_c ||
					filterObject.solvent_used_c ||
					filterObject.lastActivityDate ||
					filterObject.createdAt ||
					filterObject.siteType ||
					filterObject.contractorType ||
					filterObject.convertStatus ||
					filterObject.source ||
					filterObject.date_of_next_visit);

			if (!hasStoreFilters) {
				// Only apply route filters if no store filters exist
				if (routeFilters.site_current_status_c) {
					selector.site_current_status_c = { $eq: routeFilters.site_current_status_c };
				}
				if (routeFilters.solvent_used_c) {
					selector.solvent_used_c = { $eq: routeFilters.solvent_used_c };
				}
			}

			const defaultSelector = Object.keys(selector).length > 0 ? selector : {};
			let query = collection.find({ selector: defaultSelector });

			if (sortBy) {
				query = query.sort({ [sortBy]: 'desc' });
			} else {
				query = query.sort({ priority: 'asc' });
			}

			return query;
		},
		[debouncedSearch, filterObject, sortBy, getRouteBasedFilters]
	);

	const totalCount = useRxCountQuery<ISiteListResponse>('lms_site_list', queryConstructor);
	const { result, isFetching, fetchMore } = useRxData<ISiteListResponse>('lms_site_list', queryConstructor, {
		pageSize: pageSize || 20,
		pagination: 'Infinite',
	});

	const filteredResult = useMemo(() => {
		let filtered = result;
		if (filterObject?.location?.lat && filterObject?.location?.lng) {
			const searchLocation = {
				lat: filterObject.location.lat,
				lng: filterObject.location.lng,
			};

			const MAX_LOCATION_RADIUS_KM = 5;

			filtered = filtered.filter((site) => {
				const siteData = site._data;
				if (!siteData.latitude || !siteData.longitude) {
					return false;
				}

				const distance = calculateDistance(
					searchLocation.lat,
					searchLocation.lng,
					siteData.latitude,
					siteData.longitude
				);

				return distance <= MAX_LOCATION_RADIUS_KM;
			});
		}

		// Handle partial ID matching and prioritize ID matches
		if (debouncedSearch && debouncedSearch.trim() !== '') {
			const searchTrimmed = debouncedSearch.trim().toLowerCase();
			const searchNumber = Number(searchTrimmed);
			const isNumeric = !isNaN(searchNumber) && isFinite(searchNumber);

			if (isNumeric) {
				// Filter for partial ID matches and string field matches
				// (e.g., "132" matches ID 13240, 132, 1321, etc. OR any string field containing "132")
				const idMatches: typeof filtered = [];
				const otherMatches: typeof filtered = [];

				filtered.forEach((site) => {
					const siteData = site._data;
					const siteIdString = String(siteData.id);

					// Check if ID contains the search term (partial match)
					if (siteIdString.includes(searchTrimmed)) {
						idMatches.push(site);
					} else {
						// Check if any string field contains the search term
						const matchesStringField =
							(siteData.name?.toLowerCase()?.includes(searchTrimmed) ?? false) ||
							(siteData.site_location_c?.toLowerCase()?.includes(searchTrimmed) ?? false) ||
							(siteData.address_c?.toLowerCase()?.includes(searchTrimmed) ?? false) ||
							(siteData.applicator_phone_number_text_c?.includes(searchTrimmed) ?? false) ||
							(siteData.end_user_phone_c?.includes(searchTrimmed) ?? false) ||
							(siteData.end_user_name_c?.toLowerCase()?.includes(searchTrimmed) ?? false) ||
							(siteData.applicatorname_c?.toLowerCase()?.includes(searchTrimmed) ?? false) ||
							(siteData.whom_did_you_meet_c?.toLowerCase()?.includes(searchTrimmed) ?? false);

						if (matchesStringField) {
							otherMatches.push(site);
						}
					}
				});

				// Prioritize ID matches by putting them first
				filtered = [...idMatches, ...otherMatches];
			}
		}

		return filtered;
	}, [result, currentLocation, filterObject?.location, debouncedSearch]);

	const filteredCount = useMemo(() => {
		if (!filterObject?.location?.lat || !filterObject?.location?.lng) {
			return totalCount;
		}

		return filteredResult.length;
	}, [filteredResult, totalCount, currentLocation, filterObject?.location]);

	return {
		result: filteredResult,
		fetchMore,
		isFetching,
		totalCount: filteredCount,
	};
};
